import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getMe } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ERROR_MESSAGES = {
  domain: "That account isn't allowed. Please sign in with your company Google account.",
  not_invited: "This account hasn't been invited yet. Ask your manager for an invite, then try again.",
  unverified: "Your Google email isn't verified.",
  state: "Your sign-in session expired. Please try again.",
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    // Surface an error passed back from the OAuth callback (?error=...)
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (err) {
      setError(ERROR_MESSAGES[err] || "Sign-in failed. Please try again.");
    }

    // If user already has a valid session cookie, skip login
    getMe()
      .then((res) => {
        navigate("/dashboard", { replace: true, state: { user: res.data } });
      })
      .catch(() => {
        setChecking(false);
      });
  }, [navigate]);

  const handleLogin = () => {
    // Hand off to the backend, which starts the Google OAuth flow.
    window.location.href = `${BACKEND_URL}/api/auth/google/login`;
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-primary/10 border border-primary/20 mb-6">
            <Zap className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight uppercase text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Production Hub
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Production inventory, crewing & scheduling dashboard
          </p>
        </div>

        <div className="bg-card border border-border rounded-sm p-8">
          {error && (
            <div
              data-testid="login-error"
              className="mb-4 rounded-sm border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            >
              {error}
            </div>
          )}
          <Button
            data-testid="google-login-btn"
            onClick={handleLogin}
            className="w-full h-12 text-sm font-semibold uppercase tracking-wider bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-4">
            Secure authentication powered by Google OAuth
          </p>
        </div>
      </div>
    </div>
  );
}
